/**
 * QuestionnaireResponse → form-state hydrate helpers.
 *
 * The cascade serializes every Progress Note submission into a
 * QuestionnaireResponse whose linkIds mirror `SoapNoteDto` (see
 * `packages/middleware/src/visit/visit.utils.ts:soapNoteToQuestionnaireResponse`).
 * These helpers walk that QR back into the FE form's structured arrays so
 * the locked viewer + the edit shell render the same content the doctor
 * originally submitted — not just the narrative blob.
 *
 * Used by both `<LockedProgressNoteSections>` and the edit-mode hydrate in
 * `<ProgressNoteEdit>` so the two paths stay in lockstep. If a new field
 * lands in `SoapNoteDto` the BE QR builder + this hydrate need a matching
 * pair — keep them in mind.
 */
import type { Coding, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { VITAL_UNIT_FIELDS, resolveSavedVitalUnit } from './vitalUnits'
import type {
  AllergyItem,
  ConditionItem,
  MedicationItem,
  ObservationItem,
  ProcedureItem,
  ServiceRequestItem,
} from './progressNoteEditSchema'
import { CPT_SYSTEM, LOINC_SYSTEM } from './fhirSystems'

/**
 * Walk every item in the QR (including nested groups) and return all the
 * `valueCoding` answers for a given linkId. Used by the structured-array
 * hydrators below.
 */
export function getQuestionnaireCodingAnswers(
  response: QuestionnaireResponse | undefined,
  linkId: string,
): Coding[] {
  if (!response?.item?.length) return []
  const out: Coding[] = []
  const queue: QuestionnaireResponseItem[] = [...response.item]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (current.linkId === linkId) {
      for (const answer of current.answer ?? []) {
        if (answer.valueCoding) out.push(answer.valueCoding)
      }
    }
    if (current.item?.length) queue.push(...current.item)
  }
  return out
}

export function getQuestionnaireStringAnswer(
  response: QuestionnaireResponse | undefined,
  linkId: string,
): string {
  if (!response?.item?.length) return ''
  const queue: QuestionnaireResponseItem[] = [...response.item]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (current.linkId === linkId) {
      return current.answer?.[0]?.valueString || ''
    }
    if (current.item?.length) queue.push(...current.item)
  }
  return ''
}

/**
 * LOINC → typed-vital-linkId mapping (mirrors `draftToSoap.ts`'s
 * `LOINC_VITAL_MAP`). Used to rehydrate the QR's flat typed-vital answers
 * (`bpSystolic`, `hr`, …) into LOINC-coded ObservationItem entries so the
 * Vitals table renders proper rows.
 */
export const QR_VITAL_LINKIDS: Array<{
  linkId: string
  loinc: string
  display: string
  unit: string
  noteLinkId: string | null
}> = [
  {
    linkId: 'bpSystolic',
    loinc: '8480-6',
    display: 'Systolic blood pressure',
    unit: 'mm[Hg]',
    noteLinkId: 'bpNote',
  },
  {
    linkId: 'bpDiastolic',
    loinc: '8462-4',
    display: 'Diastolic blood pressure',
    unit: 'mm[Hg]',
    noteLinkId: 'bpNote',
  },
  { linkId: 'hr', loinc: '8867-4', display: 'Heart rate', unit: '/min', noteLinkId: 'hrNote' },
  {
    linkId: 'rr',
    loinc: '9279-1',
    display: 'Respiratory rate',
    unit: '/min',
    noteLinkId: 'rrNote',
  },
  // Defaults must match what the BE stamps (°F/lb/in); saved `…Unit` linkIds override.
  {
    linkId: 'temp',
    loinc: '8310-5',
    display: 'Body temperature',
    unit: '°F',
    noteLinkId: 'tempNote',
  },
  { linkId: 'wt', loinc: '29463-7', display: 'Body weight', unit: 'lb', noteLinkId: 'wtNote' },
  // 41653-7 already collapsed into this slot on save.
  {
    linkId: 'bloodGlucose',
    loinc: '2339-0',
    display: 'Glucose',
    unit: 'mg/dL',
    noteLinkId: 'bloodGlucoseNote',
  },
  { linkId: 'ht', loinc: '8302-2', display: 'Body height', unit: 'in', noteLinkId: 'htNote' },
  { linkId: 'o2', loinc: '59408-5', display: 'Oxygen saturation', unit: '%', noteLinkId: 'o2Note' },
  {
    linkId: 'pain',
    loinc: '72514-3',
    display: 'Pain severity',
    unit: '{score}',
    noteLinkId: 'painNote',
  },
  { linkId: 'bmi', loinc: '39156-5', display: 'Body mass index', unit: 'kg/m2', noteLinkId: null },
  {
    linkId: 'hc',
    loinc: '9843-4',
    display: 'Head circumference',
    unit: 'cm',
    noteLinkId: 'hcNote',
  },
]

/**
 * Build the `conditions[]` array from the QR. Encounter-diagnosis entries
 * come first with sequential `rank`; problem-list-item entries follow.
 */
export function hydrateConditionsFromQr(qr: QuestionnaireResponse | undefined): ConditionItem[] {
  const problemList: ConditionItem[] = getQuestionnaireCodingAnswers(qr, 'pastMedicalHistory').map(
    (coding) => ({
      id: crypto.randomUUID(),
      code: coding.code || '',
      display: coding.display || '',
      category: 'problem-list-item' as const,
      clinicalStatus: 'active' as const,
      onsetDateTime: null,
      severity: null,
      rank: null,
      note: '',
    }),
  )
  const encounterDx: ConditionItem[] = getQuestionnaireCodingAnswers(qr, 'assessment1').map(
    (coding, idx) => ({
      id: crypto.randomUUID(),
      code: coding.code || '',
      display: coding.display || '',
      category: 'encounter-diagnosis' as const,
      clinicalStatus: 'active' as const,
      onsetDateTime: null,
      severity: null,
      rank: idx + 1,
      note: '',
    }),
  )
  return [...encounterDx, ...problemList]
}

/**
 * Build the `medications[]` array. Continuation entries (medsReviewed) and
 * new prescriptions (treatmentsPrescribed) are merged with the appropriate
 * `intent`. Discontinuations are not separately represented on the QR today.
 */
export function hydrateMedicationsFromQr(qr: QuestionnaireResponse | undefined): MedicationItem[] {
  const continuation: MedicationItem[] = getQuestionnaireCodingAnswers(qr, 'medsReviewed').map(
    (coding) => ({
      id: crypto.randomUUID(),
      code: coding.code || '',
      display: coding.display || '',
      status: 'active' as const,
      intent: 'continuation' as const,
      dose: null,
      route: null,
      frequency: null,
      note: null,
    }),
  )
  const newPrescriptions: MedicationItem[] = getQuestionnaireCodingAnswers(
    qr,
    'treatmentsPrescribed',
  ).map((coding) => ({
    id: crypto.randomUUID(),
    code: coding.code || '',
    display: coding.display || '',
    status: 'active' as const,
    intent: 'new-prescription' as const,
    dose: null,
    route: null,
    frequency: null,
    note: null,
  }))
  return [...continuation, ...newPrescriptions]
}

/**
 * Build the `procedures[]` array from the QR's `proceduresAdministered`
 * coding answers (the linkId the BE QR builder + `draftToSoap.ts:487` use for
 * `SoapNoteDto.proceduresAdministered`). The QR only carries the CPT coding,
 * so `status` defaults to the schema default (`completed`) and
 * `performedDateTime` to `null` — neither is represented on the QR today. This
 * is the QR fallback for notes that lack the lossless `scribe-draft-payload`
 * blob; the blob path (`hydrateProceduresFromScribeDraft`) round-trips faithfully.
 */
export function hydrateProceduresFromQr(qr: QuestionnaireResponse | undefined): ProcedureItem[] {
  return getQuestionnaireCodingAnswers(qr, 'proceduresAdministered').map((coding) => ({
    id: crypto.randomUUID(),
    code: coding.code || '',
    display: coding.display || '',
    status: 'completed' as const,
    performedDateTime: null,
  }))
}

/**
 * Build the `serviceRequests[]` array from the QR's `testsOrdered` answers.
 * Category is inferred from the coding system (CPT → imaging, otherwise
 * laboratory). Priority + reasonText aren't on the QR — they default to
 * routine / null.
 */
export function hydrateServiceRequestsFromQr(
  qr: QuestionnaireResponse | undefined,
): ServiceRequestItem[] {
  return getQuestionnaireCodingAnswers(qr, 'testsOrdered').map((coding) => {
    const sys = coding.system || ''
    const category: ServiceRequestItem['category'] = sys.includes(CPT_SYSTEM)
      ? 'imaging'
      : 'laboratory'
    return {
      id: crypto.randomUUID(),
      system: sys || LOINC_SYSTEM,
      code: coding.code || '',
      display: coding.display || '',
      category,
      intent: 'order' as const,
      priority: 'routine' as const,
      reasonText: null,
    }
  })
}

/** Build the `allergies[]` array. */
export function hydrateAllergiesFromQr(qr: QuestionnaireResponse | undefined): AllergyItem[] {
  return getQuestionnaireCodingAnswers(qr, 'allergies').map((coding) => ({
    id: crypto.randomUUID(),
    system: coding.system || null,
    code: coding.code || null,
    display: coding.display || '',
    clinicalStatus: 'active' as const,
    reaction: null,
    severity: null,
    category: null,
    // The lossy QR path doesn't carry the chart-row pointer (PD-1141) —
    // the reopen backfill re-stamps it from the live chart.
    chartId: null,
  }))
}

/**
 * Build the structured Vitals `observations[]` from the QR's typed-vital
 * answers (`bpSystolic`, `hr`, `wt`, ...). Each non-empty value becomes a
 * LOINC-coded ObservationItem. The legacy `objective` blob is preserved as a
 * trailing exam observation by the caller (we don't include it here so the
 * helper composes cleanly when only structured vitals are wanted).
 */
export function hydrateVitalObservationsFromQr(
  qr: QuestionnaireResponse | undefined,
): ObservationItem[] {
  const out: ObservationItem[] = []
  for (const v of QR_VITAL_LINKIDS) {
    const raw = getQuestionnaireStringAnswer(qr, v.linkId)
    const trimmed = raw?.trim()
    if (!trimmed) continue
    const numeric = Number(trimmed)
    const note = v.noteLinkId
      ? getQuestionnaireStringAnswer(qr, v.noteLinkId)?.trim() || null
      : null
    const unitLinkId = VITAL_UNIT_FIELDS[v.linkId]
    const savedUnit = unitLinkId
      ? getQuestionnaireStringAnswer(qr, unitLinkId)?.trim() || undefined
      : undefined
    const unit = resolveSavedVitalUnit(v.loinc, savedUnit, v.unit)
    out.push({
      id: crypto.randomUUID(),
      code: v.loinc,
      display: v.display,
      category: 'vital-signs',
      valueQuantity: Number.isFinite(numeric) ? { value: numeric, unit } : null,
      valueString: Number.isFinite(numeric) ? null : trimmed,
      note,
    })
  }
  return out
}
